import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { empShortcodeApi, F11K } from '../../../services/empShortcodeApi';

export type Mode = 'browse' | 'add' | 'edit';

/** Map shortcode values to display labels */
export const SHORTCODE_LABELS: Record<string, string> = {
  '1': '業務',
  '2': '採購',
  '3': '實驗室',
  '4': '人事',
  '5': '業務&採購',
};

export function useEmpShortcodeForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F11K[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [code, setCode] = useState('');
  const [empId, setEmpId] = useState('');
  const [isLeader, setIsLeader] = useState(false);
  const [isOldCode, setIsOldCode] = useState(false);
  const [bizType, setBizType] = useState('');

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await empShortcodeApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setCode(current.f11k_01);
      setEmpId(current.f11k_11);
      setIsLeader(current.f11k_12);
      setIsOldCode(current.f11k_13);
      setBizType(current.f11k_14);
    }
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    setCode('');
    setEmpId('');
    setIsLeader(false);
    setIsOldCode(false);
    setBizType('1');
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    setCode(current.f11k_01);
    setEmpId(current.f11k_11);
    setIsLeader(current.f11k_12);
    setIsOldCode(current.f11k_13);
    setBizType(current.f11k_14);
  };

  // Cancel (restore)
  const cancel = () => {
    setMode('browse');
    if (current) {
      setCode(current.f11k_01);
      setEmpId(current.f11k_11);
      setIsLeader(current.f11k_12);
      setIsOldCode(current.f11k_13);
      setBizType(current.f11k_14);
    }
  };

  // Save
  const save = async () => {
    try {
      if (mode === 'add') {
        await empShortcodeApi.create({
          f11k_01: code,
          f11k_11: empId,
          f11k_12: isLeader,
          f11k_13: isOldCode,
          f11k_14: bizType,
        });
        message.success('新增成功');
      } else {
        await empShortcodeApi.update(current!.f11k_01, current!.f11k_11, {
          f11k_12: isLeader,
          f11k_13: isOldCode,
          f11k_14: bizType,
        });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const targetCode = mode === 'add' ? code : current!.f11k_01;
      const targetEmpId = mode === 'add' ? empId : current!.f11k_11;
      const idx = data.findIndex(r => r.f11k_01 === targetCode && r.f11k_11 === targetEmpId);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await empShortcodeApi.delete(current.f11k_01, current.f11k_11);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Quick search
  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f11k_01 >= searchText || r.f11k_11 >= searchText);
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    code, setCode,
    empId, setEmpId,
    isLeader, setIsLeader,
    isOldCode, setIsOldCode,
    bizType, setBizType,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
    loadRecords,
  };
}
